<template>
  <div class="bg-brand-cloud md:px-[55px]  max-w-[1280px] w-full mx-auto pb-[100px]">

    <back status="status" :page="2" :hash="postHash"></back>

    <div class="pb-4 mb-4 border-b border-brand-light-gray">
      <h1 class="hdg text-brand-wicked">Setup your intake form</h1>
      <p class="paragraph-default text-brand-medium-gray">
        Build out the sections and add concerns for your patient intake form.
      </p>
      <div v-if="errorMessage" class="mt-2 text-form-error">{{ errorMessage }}</div>
    </div>
    <div v-for="(section, index) in reactiveSections" :key="index">
      <div class="create-form_form-section">
        <div
          :class="{
            'form mb-6': !dragEnabled,
            'bg-white border-brand-light-gray rounded-[8px] p-6 border': !dragEnabled && index === showSectionDetailIndex,
            '': dragEnabled,
            }"
          >
          <div class="flex flex-col justify-between w-full gap-5 pb-3 mt-5 mb-6 border-b md:items-end md:flex-row border-brand-light-gray" v-if="index !== showSectionDetailIndex">
            <div class="">
              <p class="mb-1 font-semibold text-brand-wicked paragraph-large">
                {{ section.name }}
              </p>
              <p class="paragraph-small text-brand-medium-gray">
                {{ section.direction }}
              </p>
            </div>

            <div class="flex flex-col gap-4 md:items-center md:flex-row">
              <button class="flex items-center flex-none gap-2 font-semibold group paragraph-large paragraph-default text-brand-eggplant" @click="expandSectionDetailsEdit(index)">
                <span class="flex-none group-hover:underline group-hover:text-brand-indigo paragraph-default">Edit Section Details</span>
                <svg class='flex-none group-hover:text-brand-indigo icon icon-edit size-4 text-brand-wicked' aria-hidden='true'><use xlink:href='#icon-edit'></use></svg>
              </button>
            </div>
          </div>
          <div class="flex items-end justify-between w-full gap-5 pb-3 mt-5 mb-6 border-b border-brand-light-gray" v-else>
            <div>
              <p class="mb-1 font-semibold text-brand-wicked paragraph-large">
                Edit Section Details
              </p>
            </div>
            <div class="">
              <button class="flex items-center gap-2 font-semibold group paragraph-large paragraph-default text-brand-eggplant" @click="expandSectionDetailsEdit(index)">
                <span class="flex-none group-hover:underline group-hover:text-brand-indigo paragraph-default">Close</span>
                <svg class='rotate-45 icon size-4 icon-plus' aria-hidden='true'><use xlink:href='#icon-plus'></use></svg>
              </button>
            </div>
          </div>

          <section-detail-edit
            :section="section"
            :index="index"
            :error-message="errorMessage"
            @editSectionDetail="handleEditSectionDetail"
            v-if="showSectionDetailEdit && index === showSectionDetailIndex"
          ></section-detail-edit>

          <div
            :class="{
              'pb-6 border-b border-brand-light-gray': dragEnabled,
              '': !dragEnabled,
            }"
            >

          <concern-grid
            :concerns="section.concerns"
            :drag-enabled="dragEnabled"
            :ogindex="index"
            :show-section-detail-index="showSectionDetailIndex"
            @updateConcern="updateConcern(index, $event)"
          ></concern-grid>

            <template v-if="sections && sections.length > 0">

              <button
              v-for="(concern, concernIndex) in sections.concerns" :key="concernIndex"
              class="flex rounded-[8px] border border-brand-indigo group bg-white items-center gap-4 w-full p-3 hover:bg-brand-light-purple">
              <div class="icon-wrapper group-hover:bg-white rounded-full size-[18px] bg-brand-indigo flex items-center justify-center">
                  <svg class='icon size-[10px] group-hover:text-brand-indigo icon-plus text-white' aria-hidden='true'><use xlink:href='#icon-plus'></use></svg>
              </div>
              <span class="font-semibold paragraph-small text-brand-eggplant">
                  {{ concern.name }}
              </span>
              </button>

            </template>

            <div class="flex items-center gap-3">
              <input
                v-if="showSectionDetailEdit && index === showSectionDetailIndex"
                @click="submitSectionDetail"
                :class="{ 'border border-form-error' : errorMessage, '' : !errorMessage }"
                id="save-section" class="mt-4 w-fit-content form-submit btn-primary" type="submit" name="save_section" value="Save"
              >

              <input
                v-if="showSectionDetailEdit && index === showSectionDetailIndex"
                @click="removeSection(index)"
                :class="{ 'border border-form-error' : errorMessage, '' : !errorMessage }"
                id="remove-section" class="mt-4 w-fit-content form-submit btn-secondary" type="submit" name="remove_section" value="Delete Section"
              >
            </div>

          </div>
        </div>
      </div>

      <!-- concern popup -->
      <div :id="`create-form__new-condition-popup${index}`" class="w-[575px] mfp-hide max-w-[90vw] rounded-[8px] p-6 md:p-12 mx-auto bg-white absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2" data-section-key="">
        <button class="mfp-close size-10">
          <svg class="rotate-45 pointer-events-none size-6 icon icon-plus text-brand-medium-gray" aria-hidden="true"><use xlink:href="#icon-plus"></use></svg>
        </button>
        <p class="mb-3 text-center hdg text-brand-wicked">
          Add Concern
        </p>
        <p class="mb-4 text-center text-brand-medium-gray paragraph-default">
          Add your a concern to your section. It is recommended these concerns are only 1-2 words. You can enter multiple concerns at once using a comma seprated list.
        </p>
        <div class="form">
          <input type="hidden" name="section-key" id="section-key" value="">
          <input type="hidden" name="section-name" id="section-name" value="">

          <label for="create-form-concern-name">Concern</label>
          <input v-model="concernName" type="text" name="create-form-concern-name" id="create-form-concern-name" value="">
          <p class="field_description">e.g. “Wrinkles”</p>

          <input
            @click.prevent="addConcern(index)"
            id="submit-new-concern"
            :class="{ 'border border-form-error' : errorMessage, '' : !errorMessage }"
            class="w-full mt-4 form-submit btn-primary" type="submit" name="add_section" value="Continue">

          <div v-if="errorMessage" class="mt-2 text-form-error">{{ errorMessage }}</div>
        </div>
      </div>
    </div>

    <div class="">
      <button
        id="create-form__add-section"
        class="flex items-center pl-3 pr-5 js-init-popup btn-primary group"
        :data-modal="`#create-form__new-section-popup`"
        data-component="modal">
        <svg class='mr-2 size-4 text-brand-white icon icon-plus' aria-hidden='true'><use xlink:href='#icon-plus'></use></svg>
        Add New Section
      </button>
    </div>
    <!-- create section popup -->
    <div
      :id="`create-form__new-section-popup`"
      class="w-[775px] mfp-hide max-w-[90vw] rounded-[8px] md:py-12 md:px-16 p-6 mx-auto bg-white absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2">
        <button class="mfp-close size-10">
          <svg class="rotate-45 pointer-events-none size-6 icon icon-plus text-brand-medium-gray" aria-hidden="true"><use xlink:href="#icon-plus"></use></svg>
        </button>
        <p class="mb-3 text-center hdg text-brand-wicked">
          Add New Section
        </p>
        <p class="mb-4 text-center text-brand-medium-gray paragraph-default">
          Add your section label and any directions you'd like to feature.
        </p>

        <div class="form">
          <input type="hidden" name="create-form-section-id" id="create-form-section-id" value="<?= wp_rand() ?>">

          <label for="create-form-section-name">Section Name</label>
          <input v-model="sectionName" type="text" name="create-form-section-name" id="create-form-section-name" value="">
          <p class="field_description">e.g. "Face & Neck"</p>

          <label for="create-form-section-directions">Section Directions</label>
          <input v-model="sectionDirections" type="text" name="create-form-section-directions" id="create-form-section-directions" value="">
          <p class="field_description">e.g. "Select any concerns from this area. If you have none skip this section."</p>

          <input
            :class="{ 'border border-form-error' : errorMessage, '' : !errorMessage }"
            id="submit-new-section" class="w-full mt-4 form-submit btn-primary" type="submit" name="add_section" value="Continue" @click="addSection">

          <div v-if="errorMessage" class="mt-2 text-form-error">{{ errorMessage }}</div>
        </div>
    </div>

    <intake-footer
      :page="2"
      :saveText="saveText"
      :totalPages="totalPages"
      @triggerToDashboard="toDashboard"
      @triggerToNextPage="toStyle"
      @triggerSave="handleSaveSection"
    ></intake-footer>

    <loading :showLoading="isLoading"></loading>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue';

import axios from 'axios';

const props = defineProps({
  postId: Number,
  userId: Number,
  postHash: String,
  sections: Array,
  title: String,
  status: String,
  formColors: Object,
  emailColors: Object,
});

const reactiveSections = reactive([...props.sections]);
const dragEnabled = ref(true);

const sectionName = ref('');
const sectionDirections = ref('');
const concernName = ref('');
const isSaving = ref(false);
const saveStatus = ref('');
const isLoading = ref(false);
const errorMessage = ref('');
const showSectionDetailEdit = ref(false);
const showSectionDetailIndex = ref(-1);
const sectionDetailActive = ref('hidden');
const saveText = ref('Update');
const totalPages = ref(4);
const progress = computed(() => {
  return (2 / 4) * 100;
});

const expandSectionDetailsEdit = (index) => {

  if (showSectionDetailIndex.value === index) {
    showSectionDetailEdit.value = !showSectionDetailEdit.value;
    if (!showSectionDetailEdit.value) {
      showSectionDetailIndex.value = -1; // Hide the index if the section is closed
    }
    dragEnabled.value = true;
  } else {
    showSectionDetailIndex.value = index;
    showSectionDetailEdit.value = true;
    dragEnabled.value = false;
  }
}

const addConcern = async (index) => {
  errorMessage.value = '';
  if(concernName.value.length <= 0) {
    errorMessage.value = 'Please provide some text.';
    return;
  }

  if (reactiveSections[index].concerns.includes(concernName.value)) {
    errorMessage.value = 'This concern already exists.';
    return;
  }

  reactiveSections[index].concerns.push(concernName.value);

  // await handleSaveSection();

  concernName.value = '';
}

watch(
  () => reactiveSections.map(section => section.concerns),
  async () => {
    await handleSaveSection();
  },
  { deep: true }
);

const addSection = async () => {
  errorMessage.value = '';
  if(sectionName.value.length <= 0 || sectionDirections.value.length <= 0) {
    errorMessage.value = 'Please provide some text.';
    return;
  }

  const section = {
    name: sectionName.value,
    direction: sectionDirections.value,
    concerns: [],
  };

  reactiveSections.push(section);

  await handleSaveSection();

  sectionName.value = '';
  sectionDirections.value = '';
}

const updateConcern = (sectionIndex, updatedConcerns) => {
  reactiveSections[sectionIndex].concerns = [...updatedConcerns];
};

const toStyle = async () => {
  // if there are no concerns selected in any section, prevent the submission and show an error message
  const hasConcerns = reactiveSections.some(section => section.concerns.length > 0);
  if (!hasConcerns) {
    errorMessage.value = 'Please select at least one concern in any section before proceeding.';
    return;
  }
  await handleSaveSection();
  window.location.href="/intake/style/"+props.postHash+"?step=3";
}

const toDashboard = async () => {
  window.location.href="/";
}

const removeSection = async (index) => {
  if (reactiveSections.length > 1) {
    reactiveSections.splice(index, 1);
    showSectionDetailIndex.value = null;
    showSectionDetailEdit.value = false;
    sectionDetailActive.value = 'hidden';
    await handleSaveSection(); // Save after removing a section
  } else {
    errorMessage.value = 'You must have at least one section.';
  }
}

const handleSaveSection = async () => {
  isSaving.value = true; // Set saving state to true
  saveStatus.value = ''; // Reset save status message
  isLoading.value = true;
  saveText.value = 'Saving...';

  try {
    const response = await axios({
      method: 'POST',
      url: `${siteInfo.wpApiSettings.ll}intakes/save-section`,
      headers: {
        'X-WP-Nonce': siteInfo.wpApiSettings.nonce,
        'X-Requested-With': 'XMLHttpRequest'
      },
      data: {
        userId: props.userId,
        postId: props.postId,
        sections: reactiveSections,
      }
    });

    // Check response status and set success message
    if (response.status === 200) {
      saveStatus.value = 'Sections saved successfully!';
      saveText.value = 'Saved';
      setTimeout(() => {
        saveText.value = 'Update';
      }, 1500);
    }
  } catch (error) {
    console.error('Error updating user intro status:', error);
    saveText.value = 'Error, try again';
    saveStatus.value = 'Error saving sections. Please try again.';
    setTimeout(() => {
      saveText.value = 'Update';
    }, 2000);
  } finally {
    $.magnificPopup.close();
    isLoading.value = false;
    isSaving.value = false; // Reset saving state
  }
};

const handleEditSectionDetail = async (section) => {
  reactiveSections[section.index].name = section.name;
  reactiveSections[section.index].direction = section.direction;
}

const submitSectionDetail = async (section) => {
  showSectionDetailIndex.value = null;
  showSectionDetailEdit.value = false;
  sectionDetailActive.value = 'hidden';
  await handleSaveSection();
}
</script>
